import * as React from "react";
import styled from "react-emotion";
import {Control, Field, Icon, Label, Select, TextArea} from "bloomer";
import Card from "../../../elements/Card";
import {getTranslationResult, getTranslationTokensCount} from "../../../api/models/Translation";
import getWordsCount from "../../../utils/WordsCount";
import Button from "../../../elements/Button";
import Languages from "../../../components/model/Translation/Languages";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import WordsLabel from "../../../elements/WordsLabel";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import Spacer from "../../../elements/Spacer";
import InputWarning from "../../../elements/InputWarning";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import {INVALID_FIELD_STYLE} from "../../../utils/Constants";

const LoadingSpan = styled("span")`
  text-align: center;
  margin-left: 20px;
  vertical-align: middle;
`;

const InformationCard = styled("div")`
  border: 1px solid lightgrey;
  border-radius: 5px;
  padding: 10px;
  min-height: 30px;
`;

const LanguagesWarning = styled("span")`
  text-align: center;
  margin-left: 20px;
  line-height: 30px;
  vertical-align: middle;
  font-size: 14px;
  color: red;
`;

const LanguageControl = styled(Control)`
  display: inline-block;
  width: 270px;
`;

const LanguageLabel = styled(Label)`
  display: inline-block;
  vertical-align: bottom;
  text-align: left;
  width: 60px;
`;

const TOKENS_LIMIT = 800;

interface ITranslationModelState {
    loading: boolean;
    loadingTokensCount: boolean;
    languages: any;
    textInput: string;
    textInputInvalid: boolean;
    result: string;
    tokensCount: number;
    wordsCount: number;
    showLimitWarning: boolean;
    showSameLanguagesWarning: boolean;
    languageFrom: string;
    languageTo: string;
    languageFromInvalid: boolean;
    languageToInvalid: boolean;
    errorMessage: string;
}

class TranslationModel extends React.Component<{}, ITranslationModelState> {
    constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            loadingTokensCount: false,
            languages: [],
            textInput: "",
            textInputInvalid: false,
            result: "",
            tokensCount: 0,
            wordsCount: 0,
            showLimitWarning: false,
            showSameLanguagesWarning: false,
            languageFrom: "",
            languageTo: "",
            languageFromInvalid: false,
            languageToInvalid: false,
            errorMessage: ""
        };
    }

    public async componentDidMount () {
        const languages = await Languages.list();
        if (languages) {
            this.setState({
                languages
            });
        }
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.TRANSLATION);
    }

    public getTokensCount = async () => {
        const text = this.state.textInput.trim();
        if (text) {
            this.setState({loadingTokensCount: true});
            const response = await getTranslationTokensCount(text);
            this.setState({loadingTokensCount: false});
            if (response) {
                return response.result;
            }
        }
        return 0;
    };

    public updateCounts = async () => {
        this.setState({
            wordsCount: getWordsCount(this.state.textInput.trim())
        })
        const tokensCount = await this.getTokensCount();
        this.setState({
            tokensCount,
            showLimitWarning: tokensCount > TOKENS_LIMIT
        });
    };

    public handleInputChange = (e: any) => {
        this.setState({
            textInput: e.target.value,
            textInputInvalid: false,
            errorMessage: ""
        }, () => {
            this.updateCounts();
        });
    };

    public onSubmit = async (e: any) => {
        e.target.blur();

        if (!this.state.textInput.trim() || this.state.loading || this.state.loadingTokensCount
            || this.state.showLimitWarning || this.state.showSameLanguagesWarning
            || !this.state.languageFrom || !this.state.languageTo) {
            this.setState({result: ""});

            if (!this.state.textInput.trim()) {
                this.setState({textInputInvalid: true});
            }
            if (!this.state.languageFrom) {
                this.setState({languageFromInvalid: true});
            }
            if (!this.state.languageTo) {
                this.setState({languageToInvalid: true});
            }
            return;
        }

        this.setState({
            textInputInvalid: false,
            loading: true,
            result: "",
            errorMessage: ""
        });

        let errorOccurred = false;

        const response = await getTranslationResult(this.state.textInput.trim(), this.state.languageFrom, this.state.languageTo).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({
                    loading: false
                });
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Unable to translate."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        if (response && response.data && response.data.result) {
            this.setState({
                loading: false,
                result: response.data.result
            });
        } else {
            this.setState({
                loading: false,
                errorMessage: "Unable to translate."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.TRANSLATION);
    };

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            loadingTokensCount: false,
            textInput: "",
            textInputInvalid: false,
            result: "",
            tokensCount: 0,
            wordsCount: 0,
            showLimitWarning: false,
            showSameLanguagesWarning: false,
            languageFrom: "",
            languageTo: "",
            languageFromInvalid: false,
            languageToInvalid: false,
            errorMessage: ""
        });
    };

    public checkSelectedSameLanguages = () => {
        if (this.state.languageFrom && this.state.languageTo && this.state.languageFrom === this.state.languageTo) {
            this.setState({showSameLanguagesWarning: true});
        }
    };

    public handleSelectLanguageFromChange = (event: React.FormEvent<HTMLSelectElement>) => {
        this.setState({
            languageFrom: event.currentTarget.value,
            languageFromInvalid: false,
            showSameLanguagesWarning: false,
            errorMessage: ""
        }, this.checkSelectedSameLanguages);
    };

    public handleSelectLanguageToChange = (event: React.FormEvent<HTMLSelectElement>) => {
        this.setState({
            languageTo: event.currentTarget.value,
            languageToInvalid: false,
            showSameLanguagesWarning: false,
            errorMessage: ""
        }, this.checkSelectedSameLanguages);
    };

    public render() {
        return (
            <ModelContentContainer>
                <Card>
                    <Field style={{display: "inline-block"}}>
                        <LanguageLabel>From:</LanguageLabel>
                        <LanguageControl>
                            <Select
                                onChange={this.handleSelectLanguageFromChange}
                                value={this.state.languageFrom}
                            >
                                {
                                    this.state.languages.map((lang: any) => {
                                        return <option key={lang.code} value={lang.code}>{lang.name}</option>;
                                    })
                                }
                            </Select>
                        </LanguageControl>
                    </Field>
                    {
                        this.state.languageFromInvalid && <LanguagesWarning>Please select language.</LanguagesWarning>
                    }
                    {
                        this.state.showSameLanguagesWarning &&
                        <LanguagesWarning>The same languages are selected.</LanguagesWarning>
                    }
                    <Field>
                        <Control>
                            <TextArea
                                style={(this.state.showLimitWarning || this.state.textInputInvalid) ? INVALID_FIELD_STYLE : undefined}
                                onChange={this.handleInputChange}
                                onPaste={this.handleInputChange}
                                value={this.state.textInput}
                                placeholder="Enter your text here"
                            />
                            <div>
                                {
                                    this.state.textInputInvalid && <InputWarning>Please input a text.</InputWarning>
                                }
                                {
                                    this.state.showLimitWarning &&
                                    <InputWarning>Input size limit has been exceeded. Please decrease the
                                        size.</InputWarning>
                                }
                                <WordsLabel>{this.state.wordsCount}&nbsp;{this.state.wordsCount > 1 ? 'words' : 'word'}</WordsLabel>
                            </div>
                        </Control>
                    </Field>
                    <Field style={{display: "inline-block"}}>
                        <LanguageLabel>To:</LanguageLabel>
                        <LanguageControl>
                            <Select
                                onChange={this.handleSelectLanguageToChange}
                                value={this.state.languageTo}
                            >
                                {
                                    this.state.languages.map((lang: any) => {
                                        return <option key={lang.code} value={lang.code}>{lang.name}</option>;
                                    })
                                }
                            </Select>
                        </LanguageControl>
                    </Field>
                    {
                        this.state.languageToInvalid && <LanguagesWarning>Please select language.</LanguagesWarning>
                    }
                    {
                        this.state.showSameLanguagesWarning &&
                        <LanguagesWarning>The same languages are selected.</LanguagesWarning>
                    }
                    <Field>
                        <Control>
                            <TextArea
                                readOnly={true}
                                value={this.state.result}
                                placeholder="Output"
                            />
                        </Control>
                    </Field>
                    <div>
                        <Button onClick={this.onSubmit}>Translate</Button>
                        <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                        <Spacer/>
                        {this.state.loading && (
                            <LoadingSpan>
                                <span>Loading...</span>
                                <Icon className="fas fa-spinner fa-spin"/>
                            </LoadingSpan>
                        )}
                        {
                            this.state.errorMessage && !this.state.loading && <div>
                                <InformationCard>{this.state.errorMessage}</InformationCard>
                            </div>
                        }
                    </div>
                </Card>
            </ModelContentContainer>
        );
    }
}

export default TranslationModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/Translation/TranslationModel.tsx